import { cn } from '@/lib/utils';

/** A line of text that is edited where it is shown. It saves on blur and Enter, and Escape drops the edit.
 * `onSave` gets the trimmed text and a function that puts the saved text back, for an edit that fails or changes
 * nothing. Give it a `key` of the saved text, so that a change from elsewhere replaces what it shows. */
export const InlineTextField = ({
  value,
  label,
  placeholder,
  maxLength,
  required,
  autoFocus,
  className,
  onSave,
  onFinish,
}: {
  value: string;
  label: string;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
  autoFocus?: boolean;
  className?: string;
  onSave: (next: string, revert: () => void) => void;
  /** Called when the field loses focus, after `onSave`. */
  onFinish?: () => void;
}) => (
  <input
    type="text"
    aria-label={label}
    placeholder={placeholder}
    maxLength={maxLength}
    defaultValue={value}
    autoFocus={autoFocus}
    className={cn(
      '-mx-1.5 w-[calc(100%+0.75rem)] rounded-md bg-transparent px-1.5 py-0.5 outline-none transition-colors',
      'placeholder:text-foreground-subtle hover:bg-surface-hover focus:bg-surface focus-ring',
      className
    )}
    onFocus={(event) => event.currentTarget.select()}
    onKeyDown={(event) => {
      if (event.key === 'Enter') {
        event.currentTarget.blur();
      } else if (event.key === 'Escape') {
        event.currentTarget.value = value;
        event.currentTarget.blur();
      }
    }}
    onBlur={(event) => {
      const input = event.currentTarget;
      const next = input.value.trim();
      const revert = () => {
        input.value = value;
      };
      if (next === value || (required && next === '')) {
        revert();
      } else {
        onSave(next, revert);
      }
      onFinish?.();
    }}
  />
);
